import React, { useEffect, useRef } from 'react';
import { FixBoardLane } from '../../types/fix';
import { Spinner } from '../../design-system';
import FixBoardCard from './FixBoardCard';
import { LANE_META } from './lanes';

/** Start fetching this far before the bottom of the column scrolls into view. */
const PREFETCH_MARGIN_PX = 240;

export interface FixLaneColumnProps {
  lane: FixBoardLane;
  applicationId: string;
  /** Show each card's repository (legacy apps hold more than one). */
  showRepo?: boolean;
  /** True while the lane holds more fixes than have been loaded. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** Called as the reader approaches the bottom of the column. */
  onLoadMore?: () => void;
}

/**
 * One column of the Fixes board: the lane's name and total, and its fixes newest-first by
 * when they reached this stage.
 *
 * The column scrolls, and pages in more fixes as the reader nears the bottom rather than
 * pulling a whole lane down at once — a repository with hundreds of merged fixes only
 * sends the first handful, then the rest as they are scrolled to.
 */
const FixLaneColumn: React.FC<FixLaneColumnProps> = ({
  lane,
  applicationId,
  showRepo = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const meta = LANE_META[lane.lane];
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loaded = lane.fixes.length;

  useEffect(() => {
    if (!hasMore || !onLoadMore || typeof IntersectionObserver === 'undefined') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      // Watch inside the column's own scroll box, and fire early enough that the next
      // page usually arrives before the reader reaches the end. A first page shorter
      // than the column also leaves the sentinel visible, which fills the gap.
      { root: scrollRef.current, rootMargin: `0px 0px ${PREFETCH_MARGIN_PX}px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, loaded]);

  return (
    <section
      aria-label={meta.label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
        minHeight: 0,
        padding: 12,
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none', padding: '2px 2px 0' }}>
        <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)' }}>
          {meta.label}
        </h2>
        <span
          style={{
            padding: '1px 7px',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--surface-3)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-muted)',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {lane.count}
        </span>
      </header>

      {loaded === 0 ? (
        <p style={{ margin: 0, padding: '10px 2px 14px', fontSize: 12.5, color: 'var(--text-faint)' }}>
          {meta.empty}
        </p>
      ) : (
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            // Reserve the gutter so cards don't shift when the scrollbar appears.
            scrollbarGutter: 'stable',
          }}
        >
          {lane.fixes.map((fix) => (
            <FixBoardCard
              key={fix.id}
              fix={fix}
              lane={lane.lane}
              applicationId={applicationId}
              showRepo={showRepo}
            />
          ))}

          {hasMore && (
            <div
              ref={sentinelRef}
              style={{
                display: 'flex',
                justifyContent: 'center',
                flex: 'none',
                padding: '6px 0 2px',
                fontSize: 11,
                color: 'var(--text-faint)',
              }}
            >
              {isLoadingMore ? <Spinner size={16} /> : `${lane.count - loaded} more`}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default FixLaneColumn;
